import { useState, useCallback } from "react";
import {
    Box,
    Button,
    IconButton,
    InputAdornment,
    Paper,
    useTheme,
} from "@mui/material";
import { RoundArrowUp } from "@solar-icons/react/ssr";

import { TextFieldBorderless } from "@/common/CommonComponents";
import { WORKFLOW_REGISTRY } from "@/ai/workflowRegistry";

/**
 * AiPromptBar — an inline prompt bar for quickly launching AI workflows.
 *
 * Renders a text input with workflow shortcut chips and a send button.
 * Designed to sit on the Project page as a "quick start" entry point.
 *
 * Props:
 *  - onSendPrompt: (text: string) => void — sends a free-text prompt to the router agent
 *  - onStartWorkflow: (agentType: string) => void — opens a specific workflow
 *  - placeholder: string — input placeholder text
 */
export default function AiPromptBar({
    onSendPrompt,
    onStartWorkflow,
    placeholder = "What do you need help with today?",
}) {
    const theme = useTheme();
    const [input, setInput] = useState("");

    const trimmedInput = input.trim();
    const canSend = trimmedInput.length > 0;

    const handleSend = useCallback(() => {
        if (!canSend) return;
        onSendPrompt(trimmedInput);
        setInput("");
    }, [canSend, trimmedInput, onSendPrompt]);

    const handleKeyDown = useCallback(
        (event) => {
            if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                handleSend();
            }
        },
        [handleSend],
    );

    return (
        <Paper
            elevation={0}
            sx={{
                background: "transparent",
                border: `2px solid ${theme.palette.mode === "dark" ? "#ffffff20" : theme.palette.divider}`,
                borderRadius: "8px",
                px: 1.5,
                py: 1,
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
                transition: "border-color 0.15s ease",
                "&:focus-within": {
                    borderColor: "primary.main",
                },
            }}
        >
            {/* Text input row */}
            <TextFieldBorderless
                size="small"
                fullWidth
                multiline
                rows={3}
                placeholder={placeholder}
                value={input}
                onChange={(event) => setInput(event.target.value)}
                onKeyDown={handleKeyDown}
                sx={{ ml: -1 }}
                InputProps={{
                    startAdornment: (
                        <InputAdornment
                            position="start"
                            sx={{ mr: 1, alignSelf: "flex-start", mt: "10px" }}
                        >
                            <img
                                src="/make_ai_logo.svg"
                                alt=""
                                width={20}
                                height={20}
                                style={{ transform: "rotate(180deg)" }}
                            />
                        </InputAdornment>
                    ),
                }}
            />

            {/* Bottom row: workflow chips + send button */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    {[
                        "brief",
                        // "bid",        // hidden for this branch
                        // "bid_create", // hidden for this branch
                        "wrap_report",
                        "timeline",
                    ].map((agentType) => {
                        const config = WORKFLOW_REGISTRY[agentType];
                        if (!config) return null;
                        const Icon = config.icon;
                        return (
                            <Button
                                key={agentType}
                                startIcon={
                                    Icon ? <Icon size={14} /> : undefined
                                }
                                color="inherit"
                                variant="contained"
                                size="small"
                                onClick={() => onStartWorkflow(agentType)}
                            >
                                {config.label}
                            </Button>
                        );
                    })}
                </Box>
                <IconButton
                    size="small"
                    disabled={!canSend}
                    onClick={handleSend}
                    sx={{ opacity: canSend ? 1 : 0.4 }}
                >
                    <RoundArrowUp
                        size={20}
                        color={canSend ? undefined : "#bdbdbd"}
                    />
                </IconButton>
            </Box>
        </Paper>
    );
}
